<script setup lang="ts">
import { X } from 'lucide-vue-next'

import type { Icons } from '@/components/base/BaseIcon.vue'

export type ChipColor =
  | 'gray'
  | 'red'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'violet'
  | 'orange'
  | 'indigo'
  | 'emerald'
  | 'slate'
export type ChipSize = 'sm' | 'md' | 'lg'

withDefaults(
  defineProps<{
    icon?: Icons
    text?: string
    color?: ChipColor
    size?: ChipSize
    removable?: boolean
    textClasses?: string
    clickable?: boolean
    loading?: boolean
  }>(),
  {
    color: 'gray',
    removable: false,
    clickable: false,
    loading: false
  }
)

const emit = defineEmits<{
  (e: 'remove'): void
  (e: 'click'): void
}>()

const colorClasses: Record<ChipColor, string> = {
  gray: 'bg-badge-neutral text-badge-neutral-foreground',
  slate: 'bg-badge-neutral text-badge-neutral-foreground',
  green: 'bg-badge-success text-badge-success-foreground',
  emerald: 'bg-badge-success text-badge-success-foreground',
  yellow: 'bg-badge-warning text-badge-warning-foreground',
  red: 'bg-badge-danger text-badge-danger-foreground',
  blue: 'bg-badge-info text-badge-info-foreground',
  orange: 'bg-badge-orange text-badge-orange-foreground',
  indigo: 'bg-badge-indigo text-badge-indigo-foreground',
  violet: 'bg-badge-violet text-badge-violet-foreground'
}
</script>

<template>
  <Badge
    data-cy="base-chip"
    variant="secondary"
    :class="[
      'transition-[color,background-color,filter] duration-200',
      colorClasses[color],
      (removable || clickable) && 'hover:brightness-95',
      clickable && 'cursor-pointer'
    ]"
    @click="emit('click')"
  >
    <slot name="leading">
      <BaseIcon v-if="icon && icon !== 'IconoirInfoCircle'" :name="icon" class="size-3" />
    </slot>

    <Spinner v-if="loading" size="sm" class="size-3" />

    <slot v-if="text" name="label">
      <span v-bind="textClasses ? { class: textClasses } : {}">{{ text }}</span>
    </slot>

    <BaseIcon v-if="icon === 'IconoirInfoCircle' && !loading" :name="icon" class="size-3" />

    <button
      v-if="removable"
      type="button"
      class="ml-0.5 inline-flex items-center rounded-full p-0.5 hover:bg-black/10 transition-colors"
      aria-label="Remove"
      @click.stop="emit('remove')"
    >
      <X class="size-3" />
    </button>
  </Badge>
</template>
